import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { filter, map, take } from 'rxjs/operators';

import { NgSelectConfig } from '@ng-select/ng-select';
import { Language, LanguageStorageService, ProfileService, STORAGE_KEYS, StorageKeys, StorageService, rtlLanguageCodeList } from 'library-explorer';
import { Observable, of } from 'rxjs';
import { sessionKeys } from '../shared/constants/translation-helper.const';
import { ClassService } from './api/class.service';
import { LibraryService } from './api/library.service';
import { UserService } from './api/user.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  protected readonly localStorageKey;
  protected readonly renderer: Renderer2;

  constructor(
    @Inject(STORAGE_KEYS) protected readonly storageKeys: StorageKeys,
    protected readonly storageService: StorageService,
    protected readonly languageStorageService: LanguageStorageService,
    protected readonly libraryService: LibraryService,
    protected readonly userService: UserService,
    protected readonly config: NgSelectConfig,
    protected readonly settingsService: SettingsService,
    protected readonly classService: ClassService,
    protected readonly translateService: TranslateService,
    protected readonly rendererFactory: RendererFactory2,
    protected readonly profileService: ProfileService) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.localStorageKey = this.storageKeys.PLATFORM_LANGUAGE
    this.initialize();
  }

  public get currentLang(): string {
    return this.translateService.currentLang;
  }

  public updateUserContentLangauge(contentLanguage: string): void {
    const user = this.profileService.getCurrentProfileValue();

    if (!user) {
      return;
    }

    this.profileService.updateProfile({ id: user.id, contentLanguage })
      .subscribe(() => window.location.reload());
  }

  public updateUserInterfaceLanguage(langcode: string, shouldReload = true): void {
    this.storageService.setItem(this.localStorageKey, langcode);

    const user = this.profileService.getCurrentProfileValue();

    const updateObs = user?.id ? this.profileService.updateProfile({ id: user.id, langcode }) : of(null);

    updateObs.subscribe(() => {
      if (shouldReload) {
        window.location.reload();
        return;
      }

      this.clearLanguageBasedCache();
    });
  }

  public initLanguage(): void {
    this.getDefaultLanguage().subscribe(currentLanguage => {
      const isAdmin = this.profileService.isCurrentUserIsAdmin();
      const languages = this.languageStorageService.getLanguages()?.filter(language => language.enabled ? true : isAdmin);
      const defaultLanguage = this.languageStorageService.defaultLanguage;
      const availableLanguages = languages.map(lang => lang.code) || [];
      const languageSupported = availableLanguages.indexOf(currentLanguage) !== -1;
      const language = languageSupported ? currentLanguage : defaultLanguage;
      const isRtl = this.isRtl(language);
      this.updateHtmlLang(language)

      if (isRtl) {
        this.renderer.setAttribute(document.documentElement, 'dir', 'rtl');
      }

      if (currentLanguage === this.currentLang) {
        return;
      }

      if (!languageSupported && currentLanguage !== defaultLanguage) {
        this.updateUserInterfaceLanguage(this.languageStorageService.defaultLanguage, false);
      }

      this.translateService.use(language);
      this.updateNgSelectConfig();
      this.storageService.setItem(this.localStorageKey, language);

      if (language !== defaultLanguage) {
        this.settingsService.clearCache();
      }
    });
  }

  public toggelShowTranslationKeys(): void {
    const showTranslationKeys = sessionStorage.getItem(sessionKeys.active) === 'true';
    sessionStorage.setItem(sessionKeys.active, showTranslationKeys ? 'false' : 'true');
    location.reload();
  }

  public clearLanguageBasedCache(): void {
    this.settingsService.clearCache();
    this.libraryService.clearCache();
    this.userService.clearCache();
  }

  public async initUserPreferedContentLanguage(): Promise<Language | undefined> {
    const user = this.profileService.getCurrentProfileValue();

    if (!user || !user.id) {
      return;
    }

    const forcedContentLanguage = this.getForcedContentLanguage();

    if (forcedContentLanguage) {
      await this.profileService.updateProfile({ id: user.id, contentLanguage: forcedContentLanguage }).toPromise();
      return;
    }

    if (user.contentLanguage) {
      return;
    }

    const supportedLanguages = this.languageStorageService.getContentLanguages() || [];
    const browserLanguge = this.translateService.getBrowserLang()!;
    const isBrowserLanguageSupported = supportedLanguages.map(item => item.code).indexOf(browserLanguge) !== -1;

    if (!isBrowserLanguageSupported) {
      return;
    }

    await this.profileService.updateProfile({ id: user.id, contentLanguage: browserLanguge }).toPromise();
    this.clearLanguageBasedCache();

    return supportedLanguages.find(item => item.code === browserLanguge);
  }

  public isRtl(lang = this.currentLang): boolean {
    return rtlLanguageCodeList.indexOf(lang) !== -1;
  }

  public updateHtmlLang(language: string): void {
    if (!document.documentElement) {
      return;
    }

    this.renderer.setAttribute(document.documentElement, 'lang', language);
  }

  protected getCurrentLanguage(): string {
    const profile = this.profileService.getCurrentProfileValue();

    if (profile) {
      return profile.langcode;
    }

    return this.storageService.getItem(this.localStorageKey);
  }

  protected updateNgSelectConfig(): void {
    this.translateService.get('COMMON.no_results')
      .subscribe(translation => this.config.notFoundText = translation);
  }

  protected initialize(): void {
    this.profileService.getCurrentProfile()
      .pipe(
        filter(value => !isNil(value)),
        take(1)
      )
      .subscribe(() => {
        this.initLanguage();
      });
  }

  protected getForcedContentLanguage(): string | null {
    const queryString = window.location.search;
    const queryParams = new URLSearchParams(queryString);

    return queryParams.get('contentLang');
  }

  protected getDefaultLanguage(): Observable<string> {
    return this.settingsService.getSettings().pipe(
      map(settings => {
        const queryString = window.location.search;
        const queryParams = new URLSearchParams(queryString);
        const forcedLanguageCode = queryParams.get('lang');

        if (forcedLanguageCode) {
          return forcedLanguageCode;
        }

        return this.getCurrentLanguage() || settings.languages.defaultLanguage || this.translateService.getBrowserLang() || this.languageStorageService.defaultLanguage;
      })
    );
  }

}
